/*
  * Copyright Mission 42 Ventures, Inc. All rights reserved.
  * Unauthorized copying, distribution, or use is prohibited.
  */

import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  discoveryApiRef,
  fetchApiRef,
  useApi,
} from '@backstage/core-plugin-api';
import {
  Change,
  DryRunRepo,
  NetworkPolicyGraph,
  ServiceDetail,
  SubmitResult,
} from './types';

export type NetworkPolicyAccess = {
  allowed: boolean | undefined; // undefined while loading
  canEdit: boolean;
};

// Module-level cache: the Sidebar probe and the page share one round-trip
// (same convention as useTeamAdminAccess).
let cached: NetworkPolicyAccess | undefined;
let inflight: Promise<NetworkPolicyAccess> | undefined;

export const useNetworkPolicyAccess = (): NetworkPolicyAccess => {
  const discoveryApi = useApi(discoveryApiRef);
  const fetchApi = useApi(fetchApiRef);
  const [state, setState] = useState<NetworkPolicyAccess>(
    cached ?? { allowed: undefined, canEdit: false },
  );

  useEffect(() => {
    if (cached !== undefined) {
      setState(cached);
      return undefined;
    }
    let active = true;
    inflight =
      inflight ??
      (async () => {
        try {
          const base = await discoveryApi.getBaseUrl('network-policy');
          const res = await fetchApi.fetch(`${base}/access`);
          if (!res.ok) return { allowed: false, canEdit: false };
          const json = await res.json().catch(() => ({}));
          return { allowed: true, canEdit: json.canEdit === true };
        } catch {
          return { allowed: false, canEdit: false };
        }
      })();
    inflight.then(result => {
      if (result.allowed) cached = result;
      else inflight = undefined; // transient miss: re-probe on next mount
      if (active) setState(result);
    });
    return () => {
      active = false;
    };
  }, [discoveryApi, fetchApi]);

  return state;
};

export const useNetworkPolicyApi = () => {
  const discoveryApi = useApi(discoveryApiRef);
  const fetchApi = useApi(fetchApiRef);

  const call = useCallback(
    async (path: string, init?: RequestInit) => {
      const base = await discoveryApi.getBaseUrl('network-policy');
      const res = await fetchApi.fetch(`${base}${path}`, init);
      const body = await res.json().catch(() => ({}));
      if (!res.ok) {
        throw new Error(body.error ?? `${res.status} ${res.statusText}`);
      }
      return body;
    },
    [discoveryApi, fetchApi],
  );

  const getGraph = useCallback(
    (refresh = false): Promise<NetworkPolicyGraph> =>
      call(`/graph${refresh ? '?refresh=true' : ''}`),
    [call],
  );

  const getService = useCallback(
    (name: string): Promise<ServiceDetail> =>
      call(`/service/${encodeURIComponent(name)}`),
    [call],
  );

  const postChanges = useCallback(
    (
      changes: Change[],
      dryRun: boolean,
    ): Promise<{ dryRun?: boolean; repos?: DryRunRepo[]; results?: SubmitResult[] }> =>
      call('/changes', {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ changes, dryRun }),
      }),
    [call],
  );

  // Stable identity: consumers hang effects off this object; a fresh object
  // per render would refire them in a loop.
  return useMemo(
    () => ({ getGraph, getService, postChanges }),
    [getGraph, getService, postChanges],
  );
};
